/**
 * FirstCheckinEditor — admin editor for the first-visit check-in responses.
 * Edits db.first_checkin_content via PUT /api/admin/first-checkin-content/{code}.
 * These are the founder-written replies shown when a brand-new caregiver
 * taps an option on "What's hardest right now?" — no code change needed.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, MessageCircle } from "lucide-react";
import { OPTIONS } from "@/components/DailyCheckIn";
import { CAREGIVER_OPTIONS } from "@/components/CaregiverCheckIn";

export default function FirstCheckinEditor() {
    const [items, setItems] = useState(null);
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        api.get("/first-checkin-content")
            .then((r) => setItems(r.data?.items || {}))
            .catch(() => toast.error("Couldn't load check-in responses."));
    }, []);

    const setField = (code, field, value) =>
        setItems((m) => ({ ...m, [code]: { ...m[code], [field]: value, dirty: true } }));

    const setVariantField = (code, vi, field, value) =>
        setItems((m) => {
            const it = m[code] || {};
            const variants = [...(it.variants || [])];
            variants[vi] = { ...(variants[vi] || {}), [field]: value };
            return { ...m, [code]: { ...it, variants, dirty: true } };
        });

    const isCaregiverCode = (code) => CAREGIVER_OPTIONS.some((c) => c.code === code);

    const save = async (code) => {
        const it = items[code] || {};
        if (!(it.text || "").trim()) { toast.error("Response text can't be empty."); return; }
        setSaving(code);
        try {
            await api.put(`/admin/first-checkin-content/${code}`, {
                text: it.text.trim(),
                follow_up: (it.follow_up || "").trim(),
                ...(Array.isArray(it.quick_replies)
                    ? { quick_replies: it.quick_replies.map((q) => q.trim()).filter(Boolean) }
                    : {}),
                ...(isCaregiverCode(code)
                    ? {
                        variants: [0, 1].map((vi) => {
                            const v = (it.variants || [])[vi] || {};
                            return {
                                text: (v.text || "").trim(),
                                follow_up: (v.follow_up || "").trim(),
                                quick_replies: (Array.isArray(v.quick_replies) ? v.quick_replies : [])
                                    .map((q) => q.trim()).filter(Boolean),
                            };
                        }),
                    }
                    : {}),
            });
            setItems((m) => ({ ...m, [code]: { ...m[code], is_placeholder: false, dirty: false } }));
            toast.success("Saved — live for the next new caregiver.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally {
            setSaving(null);
        }
    };

    if (!items) {
        return (
            <section className="ml-card p-6 flex items-center gap-2 text-[#6E7A74]" data-testid="first-checkin-editor-loading">
                <Loader2 className="animate-spin" size={18} /> Loading check-in responses…
            </section>
        );
    }

    return (
        <div className="space-y-4" data-testid="first-checkin-editor">
            <header className="ml-card p-5">
                <div className="flex items-center gap-2">
                    <span className="rounded-full bg-teal-soft text-teal-dark p-1.5">
                        <MessageCircle size={14} strokeWidth={2} />
                    </span>
                    <div>
                        <h2 className="font-heading text-lg font-semibold text-[#1F2624] leading-tight">
                            First-visit check-in responses
                        </h2>
                        <p className="text-xs text-[#6E7A74]">
                            Shown when a brand-new caregiver taps an option on &quot;What&apos;s hardest right now?&quot;.
                            Aim for 3–4 sentences plus one follow-up question. Saves go live immediately.
                        </p>
                    </div>
                </div>
            </header>

            {[{ title: "About the caregiver — \u201cHow are you doing today?\u201d", opts: CAREGIVER_OPTIONS },
              { title: "About the loved one — \u201cWhat's hardest right now?\u201d", opts: OPTIONS }].map((group) => (
            <React.Fragment key={group.title}>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-[#6E7A74] pt-2">{group.title}</p>
            {group.opts.map((opt) => {
                const it = items[opt.code] || { text: "", follow_up: "" };
                return (
                    <section key={opt.code} className="ml-card p-5" data-testid={`first-checkin-editor-${opt.code}`}>
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <span className="w-8 h-8 rounded-xl bg-[#F3F8F5] flex items-center justify-center">
                                <opt.icon size={15} strokeWidth={2} className="text-teal-dark" />
                            </span>
                            <h3 className="font-heading font-semibold text-[#1F2624]">{opt.label}</h3>
                            {it.is_placeholder && (
                                <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl px-2 py-0.5"
                                    data-testid={`first-checkin-placeholder-badge-${opt.code}`}>
                                    placeholder — not yours yet
                                </span>
                            )}
                        </div>
                        <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">
                            Response (3–4 sentences)
                        </label>
                        <textarea
                            value={it.text || ""}
                            onChange={(e) => setField(opt.code, "text", e.target.value)}
                            rows={4}
                            className="ml-input w-full !rounded-2xl text-sm leading-relaxed"
                            data-testid={`first-checkin-text-${opt.code}`}
                        />
                        <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#6E7A74] mb-1 mt-3">
                            Follow-up question
                        </label>
                        <input
                            type="text"
                            value={it.follow_up || ""}
                            onChange={(e) => setField(opt.code, "follow_up", e.target.value)}
                            className="ml-input w-full text-sm"
                            data-testid={`first-checkin-followup-${opt.code}`}
                        />
                        {Array.isArray(it.quick_replies) || CAREGIVER_OPTIONS.some((c) => c.code === opt.code) ? (
                            <>
                                <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#6E7A74] mb-1 mt-3">
                                    Quick replies (one per line — end open-ended ones with “...”)
                                </label>
                                <textarea
                                    value={(it.quick_replies || []).join("\n")}
                                    onChange={(e) => setField(opt.code, "quick_replies", e.target.value.split("\n"))}
                                    rows={3}
                                    className="ml-input w-full !rounded-2xl text-sm"
                                    data-testid={`first-checkin-chips-${opt.code}`}
                                />
                            </>
                        ) : null}
                        {isCaregiverCode(opt.code) && [0, 1].map((vi) => {
                            const v = ((it.variants || [])[vi]) || {};
                            return (
                                <div
                                    key={vi}
                                    className="mt-4 rounded-2xl border border-dashed border-[#CBD5E0] p-4 bg-[#FAFAF7]"
                                    data-testid={`first-checkin-variant-${opt.code}-${vi + 2}`}
                                >
                                    <p className="text-[11px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                                        Variant {vi + 2} — optional
                                    </p>
                                    <p className="text-xs text-[#6E7A74] mt-0.5 mb-2">
                                        Leave empty and Lane keeps serving your main response. Filled variants
                                        rotate, so a returning caregiver never sees the same words twice in a row.
                                        Nothing here is ever shown until you write and save it.
                                    </p>
                                    <textarea
                                        value={v.text || ""}
                                        onChange={(e) => setVariantField(opt.code, vi, "text", e.target.value)}
                                        rows={3}
                                        placeholder="Empty — not shown to caregivers until you write it."
                                        className="ml-input w-full !rounded-2xl text-sm leading-relaxed"
                                        data-testid={`first-checkin-variant-text-${opt.code}-${vi + 2}`}
                                    />
                                    <input
                                        type="text"
                                        value={v.follow_up || ""}
                                        onChange={(e) => setVariantField(opt.code, vi, "follow_up", e.target.value)}
                                        placeholder="Follow-up question (optional — falls back to the main one)"
                                        className="ml-input w-full text-sm mt-2"
                                        data-testid={`first-checkin-variant-followup-${opt.code}-${vi + 2}`}
                                    />
                                    <textarea
                                        value={Array.isArray(v.quick_replies) ? v.quick_replies.join("\n") : ""}
                                        onChange={(e) => setVariantField(opt.code, vi, "quick_replies", e.target.value.split("\n"))}
                                        rows={2}
                                        placeholder="Quick replies, one per line (optional — falls back to the main set)"
                                        className="ml-input w-full !rounded-2xl text-sm mt-2"
                                        data-testid={`first-checkin-variant-chips-${opt.code}-${vi + 2}`}
                                    />
                                </div>
                            );
                        })}
                        <div className="mt-3 flex justify-end">
                            <button
                                type="button"
                                onClick={() => save(opt.code)}
                                disabled={saving === opt.code || !it.dirty}
                                className="ml-btn-primary !min-h-[40px] !px-5 text-sm disabled:opacity-40"
                                data-testid={`first-checkin-save-${opt.code}`}
                            >
                                {saving === opt.code
                                    ? <Loader2 className="animate-spin" size={14} />
                                    : <Save size={14} strokeWidth={1.8} />}
                                {saving === opt.code ? "Saving…" : it.dirty ? "Save" : "Saved"}
                            </button>
                        </div>
                    </section>
                );
            })}
            </React.Fragment>
            ))}
        </div>
    );
}
